import { Injectable } from '@angular/core';

const MODE_NONE = 'modeNone';
const MODE_DEFAULT = 'modeDefault';
const MODE_MATCH = 'modeMatch';

const SQL_KEYWORDS = ['FROM', 'WITH', 'SELECT', 'GROUP BY', '),'];

/**
 * Sorts the tokenizable substrings by their length DESC.
 */
const sortTokenizableSubstrings = (str: string[]) => {
  return str.sort((a, b) => {
    if (a.length > b.length) {
      return -1;
    }
    if (a.length < b.length) {
      return 1;
    }
    return 0;
  });
};

const endsWith = (str, suffix) => {
  return str.indexOf(suffix, str.length - suffix.length) !== -1;
};

export const TOKENIZE_KEYS = ['AND', 'OR', '),\n', 'WHERE', 'FROM', 'WITH', 'SELECT', 'GROUP BY'];

export type TokenizeConfig = {
  shouldTokenize: string[];
  shouldMatch: string[];
  shouldDelimitBy: string[];
  convertLiterals?: boolean;
  escapeCharacter?: string;
};

const TOKENIZE_CONFIG: TokenizeConfig = {
  shouldTokenize: TOKENIZE_KEYS,
  shouldMatch: [],
  shouldDelimitBy: TOKENIZE_KEYS,
};

@Injectable({
  providedIn: 'root',
})
export class SQLWhereParserService {
  private str: any;
  private forEachToken: any;
  private previousChr: any;
  private toMatch: any;
  private currentToken: any;
  private modeStack: any;
  private currentIndex: any;

  convertLiterals = null;
  escapeCharacter = null;
  tokenizeList = [];
  tokenizeMap: any;
  matchList = [];
  matchMap: any;
  delimiterList = [];
  delimiterMap: any;

  config: TokenizeConfig = null;

  initConfig(config: TokenizeConfig) {
    if (!config) {
      /**
       *
       * @type {{shouldTokenize: string[], shouldMatch: string[], shouldDelimitBy: string[], convertLiterals: boolean, escapeCharacter: string}}
       */
      config = { ...this.defaultConfig() };
    }

    this.convertLiterals = config.convertLiterals;
    this.escapeCharacter = config.escapeCharacter;

    /**
     * Holds the list of tokenizable substrings.
     */
    this.tokenizeList = [];

    /**
     * Holds an easy lookup map of tokenizable substrings.
     */
    this.tokenizeMap = {};

    /**
     * Holds the list of quotes to match explicit strings with.
     */
    this.matchList = [];

    /**
     * Holds an easy lookup map of quotes to match explicit strings with.
     */
    this.matchMap = {};

    /**
     * Holds the list of delimiters.
     */
    this.delimiterList = [];

    /**
     * Holds an easy lookup map of delimiters.
     *
     * @type {{}}
     */
    this.delimiterMap = {};

    /**
     * Sorts the tokenizable substrings based on their length,
     * such that "<=" will get matched before "<" does.
     */
    sortTokenizableSubstrings(config.shouldTokenize).forEach((token) => {
      if (!this.tokenizeMap[token]) {
        this.tokenizeList.push(token);
        this.tokenizeMap[token] = token;
      }
    });

    config.shouldMatch.forEach((match) => {
      if (!this.matchMap[match]) {
        this.matchList.push(match);
        this.matchMap[match] = match;
      }
    });

    config.shouldDelimitBy.forEach((delimiter) => {
      if (!this.delimiterMap[delimiter]) {
        this.delimiterList.push(delimiter);
        this.delimiterMap[delimiter] = delimiter;
      }
    });
    this.config = config;
  }

  initParameters(str: string, forEachToken: Function): void {
    /**
     * The string to tokenize.
     */
    this.str = str;

    /**
     * The function to call for teach token.
     *
     * @type {Function}
     */
    this.forEachToken = forEachToken;

    /**
     * The previous character consumed.
     *
     * @type {string}
     */
    this.previousChr = '';

    /**
     * The current quote to match.
     *
     * @type {string}
     */
    this.toMatch = '';

    /**
     * The current token being created.
     *
     * @type {string}
     */
    this.currentToken = '';

    /**
     * Keeps track of the current "mode" of tokenization.
     * The tokenization rules are different depending if you are tokenizing an explicit string (surrounded by quotes),
     * versus a non-explicit string (not surrounded by quotes).
     *
     * @type {*[]}
     */
    this.modeStack = [MODE_NONE];
    this.currentIndex = 0;
  }

  getCurrentMode() {
    return this.modeStack[this.modeStack.length - 1];
  }

  setCurrentMode(mode: string) {
    return this.modeStack.push(mode);
  }

  completeCurrentMode() {
    const currentMode = this.getCurrentMode();
    if (currentMode === MODE_DEFAULT) {
      this.pushDefaultModeTokenizables();
    }

    /**
     * Don't push out empty tokens, unless they were an explicit string, e.g. ""
     */
    if ((currentMode === MODE_MATCH && this.currentToken === '') || this.currentToken !== '') {
      this.push(this.currentToken);
    }
    this.currentToken = '';

    return this.modeStack.pop();
  }

  push(token: any) {
    let surroundedBy = '';
    if (this.convertLiterals && this.getCurrentMode() !== MODE_MATCH) {
      /**
       * Convert the string version of literals into their literal form.
       */
      switch (token.toLowerCase()) {
        case 'null':
          token = null;
          break;
        case 'true':
          token = true;
          break;
        case 'false':
          token = false;
          break;
        default:
          if (isFinite(token)) {
            token = Number(token);
          }
          break;
      }
    } else {
      /**
       * The purpose of also transmitting the surroundedBy quote is to inform whether or not the token was an explicit string,
       * versus a non-explicit string, e.g. "=" vs. =
       * @type {string}
       */
      surroundedBy = this.toMatch;
    }

    if (this.forEachToken) {
      this.forEachToken(token, surroundedBy, this.currentIndex);
    }
    return token;
  }

  tokenize() {
    let index = 0;
    while (index < this.str.length) {
      this.currentIndex = index;
      this.consume(this.str.charAt(index++));
    }
    while (this.getCurrentMode() !== MODE_NONE) {
      this.completeCurrentMode();
    }
  }

  consume(chr: string): void {
    this[this.getCurrentMode()](chr);
    this.previousChr = chr;
  }

  [MODE_NONE](chr: string) {
    if (!this.matchMap[chr]) {
      this.setCurrentMode(MODE_DEFAULT);
      return this.consume(chr);
    }

    this.setCurrentMode(MODE_MATCH);
    this.toMatch = chr;
  }

  [MODE_DEFAULT](chr: string) {
    /**
     * If we encounter a delimiter, its time to push out the current token.
     */
    if (this.delimiterMap[chr]) {
      return this.completeCurrentMode();
    }

    /**
     * If we encounter a quote, only push out the current token if there's a sub-token directly before it.
     */
    if (this.matchMap[chr]) {
      let tokenizeIndex = 0;
      while (tokenizeIndex < this.tokenizeList.length) {
        if (endsWith(this.currentToken, this.tokenizeList[tokenizeIndex++])) {
          this.completeCurrentMode();
          return this.consume(chr);
        }
      }
    }
    this.currentToken += chr;
    return this.currentToken;
  }

  pushDefaultModeTokenizables() {
    let tokenizeIndex = 0;
    let lowestIndexOfTokenize = Infinity;
    let toTokenize = null;

    /**
     * Iterate through the list of tokenizable substrings.
     */
    while (this.currentToken && tokenizeIndex < this.tokenizeList.length) {
      const tokenize = this.tokenizeList[tokenizeIndex++];
      const indexOfTokenize = this.currentToken.indexOf(tokenize);

      /**
       * Find the substring closest to the beginning of the current token.
       */
      if (indexOfTokenize !== -1 && indexOfTokenize < lowestIndexOfTokenize) {
        lowestIndexOfTokenize = indexOfTokenize;
        toTokenize = tokenize;
      }
    }

    /**
     * No substrings to tokenize.  You're done.
     */
    if (!toTokenize) {
      return;
    }

    /**
     * A substring was found, but not at the very beginning of the string, e.g. A=B, where "=" is the substring.
     * This will push out "A" first.
     */
    if (lowestIndexOfTokenize > 0) {
      this.push(this.currentToken.substring(0, lowestIndexOfTokenize));
    }

    /**
     * Push out the substring, then modify the current token to be everything past that substring.
     * Recursively call this function again until there are no more substrings to tokenize.
     */
    if (lowestIndexOfTokenize !== -1) {
      this.push(toTokenize);
      this.currentToken = this.currentToken.substring(lowestIndexOfTokenize + toTokenize.length);
      return this.pushDefaultModeTokenizables();
    }
  }

  [MODE_MATCH](chr: string): string {
    if (chr === this.toMatch) {
      if (this.previousChr !== this.escapeCharacter) {
        return this.completeCurrentMode();
      }
      this.currentToken = this.currentToken.substring(0, this.currentToken.length - 1);
    }

    this.currentToken += chr;
    return this.currentToken;
  }

  tokenizeThis(str: string, forEachToken: Function): any {
    this.initParameters(str, forEachToken);
    return this.tokenize();
  }

  /**
   *
   * @returns {{shouldTokenize: string[], shouldMatch: string[], shouldDelimitBy: string[], convertLiterals: boolean, escapeCharacter: string}}
   */
  defaultConfig(): TokenizeConfig {
    return TOKENIZE_CONFIG;
  }

  parseAndGetWhereClauseStatements(lines: string[]): string[] {
    if (!lines || lines?.length === 0) {
      return [];
    }
    let whereClauseStatements = [];
    while (lines.findIndex((x: string) => x?.trim()?.toUpperCase() === 'WHERE') > 0) {
      // to seperate out the lines between WHERE clause and the next SQL Keyword
      const fromIndex = lines.findIndex((x) => x?.trim()?.toUpperCase() === 'WHERE');
      const toIndex = lines
        .slice(fromIndex)
        .findIndex((x: string) => SQL_KEYWORDS.find((y: string) => y === x?.trim()?.toUpperCase()));
      let linesToAdd = [];
      if (toIndex > 0) {
        linesToAdd = lines.slice(fromIndex + 1, fromIndex + toIndex);
      } else {
        linesToAdd = lines.slice(fromIndex + 1);
      }
      whereClauseStatements = [...whereClauseStatements, ...linesToAdd];
      lines = lines.splice(fromIndex + 1);
    }
    return this.removeRedundantParanthesis(whereClauseStatements);
  }

  removeRedundantParanthesis(statements: string[]): string[] {
    const filtered = statements.filter((x) => TOKENIZE_KEYS.findIndex((y) => y === x?.trim()?.toUpperCase()) < 0);
    return filtered.map((statement) => {
      const l1 = (statement.match(/\(/g) || []).length;
      const l2 = (statement.match(/\)/g) || []).length;
      if (l1 !== l2) {
        let str = '';
        const i = statement.lastIndexOf(l1 > l2 ? '(' : ')');
        str = l2 > l1 ? statement.slice(0, i) : statement.slice(i + 1, statement.length);
        if (!str?.trim()?.split('\n')[1] || !str?.trim()?.split('\n')[0]) {
          str = str.replace('\n', '').trim();
        }
        return str;
      }
      return statement;
    });
  }
}
